import axios from 'axios';

export interface IShopifyStore {
  store_url: string;
  access_token: string;
}

interface ShopifyProduct {
  id: string;
  title: string;
  body_html: string;
  handle: string;
  tags: string[];
  variants: any[];
  images: any[];
  vendor: string;
}

interface ShopifyProductResponse {
  product: ShopifyProduct;
}

interface ShopifyProductsResponse {
  products: ShopifyProduct[];
}

export interface UpdateProductData {
  title: string;
  body_html: string;
  bulletPoints: string[];
  pageTitle?: string;
  metaDescription?: string;
}

interface CircuitBreakerState {
  failures: number;
  lastFailure: number;
  isOpen: boolean;
}

export class ShopifyService {
  private static instance: ShopifyService;
  private stores: { [key: string]: IShopifyStore } = {};
  private baseUrl = process.env.NODE_ENV === 'development'
    ? 'http://localhost:8888/.netlify/functions/shopify'
    : '/.netlify/functions/shopify';
  private requestQueue: Promise<unknown> = Promise.resolve();
  private lastRequestTime: number = 0;
  private circuitBreaker: CircuitBreakerState = {
    failures: 0,
    lastFailure: 0,
    isOpen: false
  };

  // Rate limiting constants
  private readonly BASE_DELAY = 500; // 500ms between requests (2 req/sec)
  private readonly BATCH_DELAY = 500; // 500ms between batches
  private readonly BATCH_SIZE = 2; // Process 2 items at a time
  private readonly MAX_RETRIES = 5;
  private readonly CIRCUIT_BREAKER_THRESHOLD = 10;
  private readonly CIRCUIT_BREAKER_RESET_TIME = 30000; // 30 seconds

  private constructor() {}

  private getRandomJitter(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1) + min);
  }

  private calculateBackoff(retryAttempt: number, baseDelay: number = this.BASE_DELAY): number {
    // Exponential backoff with jitter
    const exponentialDelay = Math.min(baseDelay * Math.pow(2, retryAttempt), 10000);
    const jitter = this.getRandomJitter(-100, 100);
    return exponentialDelay + jitter;
  }

  private async checkCircuitBreaker(): Promise<void> {
    if (!this.circuitBreaker.isOpen) {
      return;
    }

    const now = Date.now();
    if (now - this.circuitBreaker.lastFailure >= this.CIRCUIT_BREAKER_RESET_TIME) {
      // Reset circuit breaker after cooling period
      this.circuitBreaker = {
        failures: 0,
        lastFailure: 0,
        isOpen: false
      };
    } else {
      throw new Error('Circuit breaker is open. Too many failures detected.');
    }
  }

  private updateCircuitBreaker(failed: boolean): void {
    const now = Date.now();
    
    if (failed) {
      this.circuitBreaker.failures++;
      this.circuitBreaker.lastFailure = now;
      
      if (this.circuitBreaker.failures >= this.CIRCUIT_BREAKER_THRESHOLD) {
        this.circuitBreaker.isOpen = true;
        
      }
    } else {
      // Reset on successful request
      this.circuitBreaker.failures = Math.max(0, this.circuitBreaker.failures - 1);
    }
  }

  private async enforceRateLimit(): Promise<void> {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    
    if (timeSinceLastRequest < this.BASE_DELAY) {
      const waitTime = this.BASE_DELAY - timeSinceLastRequest;
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    
    this.lastRequestTime = Date.now();
  }

  // Add delay between requests to respect rate limits
  private async enqueueRequest<T>(request: () => Promise<T>): Promise<T> {
    let retries = 0;

    const executeWithRetry = async (): Promise<T> => {
      try {
        await this.checkCircuitBreaker();
        await this.enforceRateLimit();

        const result = await request();
        this.updateCircuitBreaker(false);
        return result;
      } catch (error: any) {
        
        this.updateCircuitBreaker(true);

        if (retries >= this.MAX_RETRIES) {
          throw new Error(`Max retries (${this.MAX_RETRIES}) exceeded`);
        }

        if (error.response?.status === 429) {
          const retryAfter = parseInt(error.response.headers['retry-after'] || '2', 10);
          const backoffDelay = Math.max(
            retryAfter * 1000,
            this.calculateBackoff(retries)
          );
          
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
          retries++;
          return executeWithRetry();
        }

        if (error.code === 'ECONNRESET' || error.code === 'ETIMEDOUT') {
          const backoffDelay = this.calculateBackoff(retries);
          
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
          retries++;
          return executeWithRetry();
        }

        throw error;
      }
    };

    return this.requestQueue.then(() => {
      const execution = executeWithRetry();
      this.requestQueue = execution.catch(() => {});
      return execution;
    });
  }

  static getInstance(): ShopifyService {
    if (!ShopifyService.instance) {
      ShopifyService.instance = new ShopifyService();
    }
    return ShopifyService.instance;
  }

  private loadStoreConfigs() {
    if (Object.keys(this.stores).length > 0) {
      return;
    }

    const storesConfig = process.env.REACT_APP_SHOPIFY_STORES;
    if (storesConfig) {
      try {
        this.stores = JSON.parse(storesConfig);
      } catch (error) {
        
      }
    }
  }

  getStoreConfig(storeName: string): IShopifyStore {
    this.loadStoreConfigs();
    
    const normalizedStoreName = storeName.toLowerCase().replace(/\s+/g, '-');
    const store = this.stores[normalizedStoreName];
    
    if (!store) {
      throw new Error(`Store configuration not found for: ${storeName}`);
    }
    
    return store;
  }

  private findDescriptionMetafield(metafields: any[]): string {
    if (!metafields?.length) return '';
    
    const descriptionMetafield = metafields.find(
      m => m.namespace === 'global' && m.key === 'description_tag'
    );
    
    return descriptionMetafield?.value || '';
  }

  private async makeRequest<T = any>(store: IShopifyStore, endpoint: string, method: string = 'GET', params: Record<string, unknown> = {}, data?: unknown): Promise<{ data: T; headers: Record<string, string> }> {
    const makeAttempt = async (): Promise<{ data: T; headers: Record<string, string> }> => {
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Shopify-Store-URL': store.store_url,
        'X-Shopify-Access-Token': store.access_token
      };

      const response = await axios({
        method,
        url: `${this.baseUrl}${endpoint}`,
        headers,
        params,
        data
      });

      return {
        data: response.data,
        headers: response.headers as Record<string, string>
      };
    };

    return this.enqueueRequest(makeAttempt);
  }

  async getProductByHandle(store: IShopifyStore, handle: string): Promise<{ product: ShopifyProduct }> {
    const { data: productResponse } = await this.makeRequest<ShopifyProductsResponse>(
      store,
      '/products.json',
      'GET',
      { handle }
    );

    if (!productResponse.products?.length) {
      throw new Error(`Product not found with handle: ${handle}`);
    }

    const product = productResponse.products[0];

    const { data: metafieldsResponse } = await this.makeRequest<{ metafields: any[] }>(
      store,
      `/products/${product.id}/metafields.json`,
      'GET'
    );

    const formattedProduct = {
      ...product,
      meta_description: this.findDescriptionMetafield(metafieldsResponse.metafields),
      metafields: metafieldsResponse.metafields || []
    };

    return { product: formattedProduct };
  }

  private async fetchProductsWithMetafields(store: IShopifyStore, products: ShopifyProduct[]): Promise<ShopifyProduct[]> {
    const result = [];
    
    for (let i = 0; i < products.length; i += this.BATCH_SIZE) {
      const batch = products.slice(i, i + this.BATCH_SIZE);
      
      const batchPromises = batch.map(product =>
        this.makeRequest<{ metafields: any[] }>(
          store,
          `/products/${product.id}/metafields.json`,
          'GET'
        ).then(response => response.data)
      );

      const batchResponses = await Promise.all(batchPromises);
      
      result.push(...batch.map((product, index) => ({
        ...product,
        meta_description: this.findDescriptionMetafield(batchResponses[index].metafields),
        metafields: batchResponses[index].metafields || []
      })));

      if (i + this.BATCH_SIZE < products.length) {
        await new Promise(resolve => setTimeout(resolve, this.BATCH_DELAY));
      }
    }

    return result;
  }

  async getProducts(store: IShopifyStore, limit: number = 50): Promise<{ products: ShopifyProduct[] }> {
    const { data: response } = await this.makeRequest<ShopifyProductsResponse>(
      store,
      '/products.json',
      'GET',
      { limit }
    );

    const productsWithMeta = await this.fetchProductsWithMetafields(store, response.products);
    return { products: productsWithMeta };
  }

  async searchProducts(store: IShopifyStore, searchQuery: string, limit: number = 50): Promise<{ products: ShopifyProduct[] }> {
    const { data: response } = await this.makeRequest<ShopifyProductsResponse>(
      store,
      '/products.json',
      'GET',
      { limit, title: searchQuery }
    );

    const productsWithMeta = await this.fetchProductsWithMetafields(store, response.products);
    return { products: productsWithMeta };
  }

  private extractNextPageCursor(linkHeader: string): string | null {
    const nextLink = linkHeader.split(',').find(link => link.includes('rel="next"'));
    if (!nextLink) return null;
    
    const match = nextLink.match(/[?&]page_info=([^>&]*)/);
    return match ? match[1] : null;
  }

  async getTotalProductCount(store: IShopifyStore): Promise<number> {
    try {
      
      const { data: response } = await this.makeRequest<{ count: number }>(
        store,
        '/products/count',
        'GET'
      );
      
      return response.count;
    } catch (error: any) {
      
      throw error;
    }
  }

  async bulkImportProducts(store: IShopifyStore, limit: number = 50): Promise<{ products: ShopifyProduct[], hasNextPage: boolean, nextPageCursor: string | null }> {
    const params: Record<string, unknown> = {
      limit: limit || 250,
      fields: 'id,title,body_html,handle,tags,variants,images,vendor'
    };

    const { data: response, headers } = await this.makeRequest<ShopifyProductsResponse>(
      store,
      '/products.json',
      'GET',
      params
    );

    const linkHeader = headers?.link || '';
    const nextPageCursor = this.extractNextPageCursor(linkHeader);

    const products = response.products || [];
    const productsWithMeta = await this.fetchProductsWithMetafields(store, products);
    
    const hasNextPage = !!nextPageCursor;
    
    return { 
      products: productsWithMeta,
      hasNextPage,
      nextPageCursor
    };
  }

  async updateProduct(store: IShopifyStore, productId: string, data: UpdateProductData): Promise<{ product: any }> {
    try {
      const bulletPointsHtml = data.bulletPoints.length > 0
        ? `<div class="product-bullet-points" style="margin: 0 0 2em 0;">
            <ul style="margin: 0; padding-left: 1.5em; list-style-type: disc;">
              ${data.bulletPoints.map(point => {
                const formattedPoint = point.replace(/\b[A-Z]{2,}\b/g, match => `<strong>${match}</strong>`);
                return `<li style="margin: 0.5em 0; line-height: 1.6;">${formattedPoint}</li>`;
              }).join('\n')}
            </ul>
          </div>`
        : '';

      const formattedDescription = data.body_html
        .trim()
        .replace(/\r\n/g, '\n')
        .split(/\n\s*\n/)
        .filter(para => para.trim())
        .map(para => `<p style="margin: 0 0 1em; line-height: 1.6;">${para.trim()}</p>`)
        .join('\n');

      const fullHtml = `<div class="shopify-product-content" style="margin: 0; padding: 0;">
        ${bulletPointsHtml}
        <div class="product-description" style="margin: 0;">
          ${formattedDescription}
        </div>
      </div>`;

      const updateData = {
        product: {
          title: data.title,
          body_html: fullHtml,
          metafields: [
            {
              namespace: 'global',
              key: 'title_tag',
              value: data.pageTitle || data.title,
              type: 'single_line_text_field'
            },
            {
              namespace: 'global',
              key: 'description_tag',
              value: data.metaDescription || data.body_html.substring(0, 157).trim() + '...',
              type: 'single_line_text_field'
            }
          ]
        }
      };


      const { data: response } = await this.makeRequest<ShopifyProductResponse>(
        store,
        `/products/${productId}.json`,
        'PUT',
        {},
        updateData
      );

      if (!response?.product) {
        throw new Error('Invalid response from Shopify API');
      }

      return { product: response.product };
    } catch (error: any) {
      
      if (error.response?.data?.errors) {
        throw new Error(`Shopify API error: ${JSON.stringify(error.response.data.errors)}`);
      }
      
      throw error;
    }
  }
}

export const shopifyService = ShopifyService.getInstance();